import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Loader2, ArrowLeft, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import ProjectScoreCard from "@/components/projects/ProjectScoreCard";
import ProjectCoach from "@/components/projects/ProjectCoach";
import MatchedGrants from "@/components/projects/MatchedGrants";
import GrantFitDialog from "@/components/projects/GrantFitDialog";
import RubricPanel from "@/components/projects/RubricPanel";
import LessonsLearnedPanel from "@/components/projects/LessonsLearnedPanel";
import HistoryTab from "@/components/provenance/HistoryTab";
import ShareDashboardCard from "@/components/share/ShareDashboardCard";
import { toast } from "sonner";

const ProjectDetail = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [fitGrant, setFitGrant] = useState<any | null>(null);

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [authLoading, user, navigate]);

  const { data: project, isLoading } = useQuery({
    queryKey: ["project", projectId],
    queryFn: async () => {
      const { data: proj, error: pErr } = await supabase
        .from("projects")
        .select("*")
        .eq("id", projectId)
        .maybeSingle();
      if (pErr) {
        toast.error(pErr.message);
        throw pErr;
      }
      if (!proj) return null;
      const { data: idea, error: iErr } = await supabase
        .from("ideas")
        .select("id, title, description, category, image_url")
        .eq("id", proj.idea_id)
        .maybeSingle();
      if (iErr) {
        toast.error(iErr.message);
        throw iErr;
      }
      return { ...proj, ideas: idea };
    },
    enabled: !!projectId && !!user,
  });

  const { data: profile } = useQuery({
    queryKey: ["my-profile-pro", user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("profiles")
        .select("is_pro")
        .eq("user_id", user!.id)
        .maybeSingle();
      return data;
    },
    enabled: !!user,
  });

  const { data: latestScore, isLoading: scoreLoading } = useQuery({
    queryKey: ["project-overall-score", projectId],
    queryFn: async () => {
      const { data } = await supabase
        .from("project_scores")
        .select("*")
        .eq("project_id", projectId)
        .eq("score_type", "overall")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      return data;
    },
    enabled: !!projectId,
    refetchInterval: (q) => (q.state.data ? false : 4000),
  });

  const rescore = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke("score-project", {
        body: { project_id: projectId, mode: "overall" },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["project-overall-score", projectId] });
      toast.success("Score updated.");
    },
    onError: (e: any) => toast.error(e?.message ?? "Scoring failed"),
  });

  useEffect(() => {
    if (project?.ideas) {
      document.title = `${(project.ideas as any).title} | Project | emrkt.co`;
    }
  }, [project]);

  if (authLoading || isLoading || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <SiteHeader />
        <main className="pt-32 pb-20 container mx-auto px-6 text-center">
          <h1 className="text-2xl font-bold mb-2">Project not found</h1>
          <Button onClick={() => navigate("/profile")}>Back to my profile</Button>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const idea = project.ideas as any;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <button
            onClick={() => navigate(`/ideas/${idea.id}`)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <ArrowLeft className="h-4 w-4" /> Back to idea
          </button>

          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-primary mb-1">
                <Lightbulb className="h-3.5 w-3.5" /> Project
              </div>
              <h1 className="font-display text-3xl font-bold">{idea.title}</h1>
              <p className="text-sm text-muted-foreground mt-1">{idea.category}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 space-y-5">
              <ProjectScoreCard
                score={latestScore?.score ?? null}
                summary={latestScore?.summary ?? null}
                isLoading={scoreLoading}
                isRescoring={rescore.isPending}
                onRescore={() => rescore.mutate()}
                lastUpdated={latestScore?.created_at ?? null}
              />

              {Array.isArray(latestScore?.suggestions) && (latestScore!.suggestions as any[]).length > 0 && (
                <div className="bg-card border border-border rounded-xl p-6">
                  <h3 className="font-display text-lg font-semibold mb-4">Improvement Actions</h3>
                  <div className="space-y-3">
                    {(latestScore!.suggestions as any[]).map((s, i) => (
                      <div key={i} className="border border-border rounded-lg p-4">
                        <div className="text-sm font-semibold mb-1">{s.title}</div>
                        <div className="text-sm text-muted-foreground">{s.detail}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <MatchedGrants projectId={projectId!} onScoreAgainst={(g) => setFitGrant(g)} />

              <RubricPanel projectId={projectId!} isPro={!!profile?.is_pro} />

              <LessonsLearnedPanel
                projectId={projectId!}
                isOwner={project.user_id === user.id}
                ownerUserId={user.id}
              />

              <HistoryTab kind="project" id={projectId!} />
            </div>

            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-32">
                <div className="space-y-5">
                  {project.user_id === user.id && (
                    <ShareDashboardCard
                      kind="project"
                      entityId={project.id}
                      initialEnabled={!!(project as any).is_public_share}
                      initialSlug={(project as any).public_share_slug ?? null}
                    />
                  )}
                  <ProjectCoach projectId={projectId!} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
      <GrantFitDialog
        open={!!fitGrant}
        onOpenChange={(o) => !o && setFitGrant(null)}
        projectId={projectId!}
        grant={fitGrant}
      />
    </div>
  );
};

export default ProjectDetail;